/**
 * appSyncApplicationFormClient — the live `ApplicationFormClient` over AppSync
 * (task 19.3).
 *
 * Implements the exact same `ApplicationFormClient` interface the mock does
 * (task 19.2), so flipping a surface mock→live changes nothing in the
 * hooks/components. Every Application_Form operation is Cognito-authorized
 * (`userPool`); there is no public/apiKey surface for application/identity data
 * (the schema deliberately omits `@aws_api_key` on ApplicationResponse /
 * PageApproval / Person).
 *
 * AWSJSON handling (AppSync scalar semantics): AWSJSON fields cross the wire as
 * JSON-encoded *strings*. Outbound (`snapshot`, `values`, `approvalDetails`, the
 * `getJudgeProjection` payload) are `JSON.parse`d here into their structured
 * shapes; inbound AWSJSON inputs (`SaveDraftInput.values`, `SubmitInput.values`,
 * `SetPageApprovalInput.approvalDetails`) are `JSON.stringify`d before they are
 * sent. The pure backend modules own the canonical serialization; this client
 * only encodes/decodes the transport.
 *
 * Validates: application-form-builder Requirements 1.1, 2.1, 6.1, 7.1, 9.1, 14.4
 */

import { generateClient } from 'aws-amplify/api';

import type { ApplicationFormClient } from './applicationFormClient';
import type {
  ApplicationForm,
  ApplicationResponse,
  ApplicationResponseSummary,
  ApplicationSection,
  ApprovalDetails,
  ApprovalDetailVisibilityConfig,
  CreateDocumentReferenceInput,
  Destination,
  DocumentCatalogEntry,
  DocumentRef,
  FormField,
  FormPage,
  FormVersion,
  FormVersionSnapshot,
  GetDocumentUploadUrlInput,
  ManualLockOverride,
  OnboardingDestinationKind,
  PageApproval,
  PresignedUpload,
  RenderedResponse,
  ResponseValues,
  ResponseValuesRow,
  SubmitResult,
  Title,
} from './types';
import * as ops from './operations';
import { resolveCallerPersonId } from './callerIdentity';

// The Amplify client's generic surface is heavy; we only call `.graphql(...)`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cached: any = null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function client(): any {
  if (!cached) cached = generateClient();
  return cached;
}

/**
 * Normalize an Amplify GraphQL rejection into a real `Error` carrying the
 * backend's message. Amplify's `client.graphql(...)` REJECTS GraphQL errors as a
 * plain `{ data, errors }` object (NOT an `Error` instance); lifting
 * `errors[].message` surfaces the actual backend reason instead of a generic
 * fallback (the same normalization the Merit live client uses).
 */
function normalizeGraphqlError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object' && 'errors' in err) {
    const errors = (err as { errors?: { message?: string }[] }).errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return new Error(errors.map((e) => e?.message ?? String(e)).join('; '));
    }
  }
  return new Error(typeof err === 'string' ? err : JSON.stringify(err));
}

/** Issue one operation and return the named field from `data`. */
async function run<T>(
  query: string,
  variables: Record<string, unknown>,
  field: string,
): Promise<T> {
  let resp: { data: Record<string, T>; errors?: { message: string }[] };
  try {
    resp = (await client().graphql({ query, variables, authMode: 'userPool' })) as {
      data: Record<string, T>;
      errors?: { message: string }[];
    };
  } catch (err) {
    throw normalizeGraphqlError(err);
  }
  if (resp.errors && resp.errors.length > 0) {
    throw new Error(resp.errors.map((e) => e.message).join('; '));
  }
  return resp.data[field];
}

/**
 * Decode an AWSJSON scalar. AppSync returns AWSJSON as a JSON-encoded string;
 * defensively accept an already-parsed object too (some transports pre-parse).
 */
function decodeAwsJson<T>(raw: unknown): T {
  if (typeof raw === 'string') return JSON.parse(raw) as T;
  return raw as T;
}

// ---------------------------------------------------------------------------
// Wire shapes (the raw selection-set results before AWSJSON decoding).
// ---------------------------------------------------------------------------

interface RawOnboardingDestination {
  kind: OnboardingDestinationKind;
  competitionId?: string | null;
}

interface RawFormVersion {
  id: string;
  formSchemaId: string;
  versionNumber: number;
  competitionId?: string | null;
  snapshot: string;
  createdAt: string;
}

interface RawApplicationResponse {
  applicantPersonId: string;
  competitionId: string;
  formSchemaId: string;
  formVersionId: string;
  submissionStatus: ApplicationResponse['submissionStatus'];
  values: string;
  titlePreferences?: string[] | null;
  submittedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * The director "request changes" marker (applicant-application-flow). True when
   * a SUBMITTED application was returned to DRAFT for edits, so the applicant
   * surface resolves CHANGES_REQUESTED; null/absent when never returned.
   */
  reviewReturn?: boolean | null;
  /**
   * The contestant-visible director return note (application-decision-feedback
   * Req 2.4); null when no note accompanied the return / on write receipts. The
   * director-only `internalRejectNote` is deliberately NOT selectable on this
   * owner-facing shape (structural R5 guarantee).
   */
  returnFeedbackNote?: string | null;
  /** Resolved server-side for the director review list only (Req 9.1); null elsewhere. */
  applicantName?: string | null;
  /** Resolved server-side for the director review list only; null elsewhere. */
  divisionKey?: string | null;
  /** Resolved from Participation by getMyApplicationResponse once approved; null before. */
  approvedTitle?: string | null;
  /** season-phase-deadlines (Req 16): the Director-set manual lock override; null = date-governed. */
  manualLockOverride?: ManualLockOverride | null;
  /** season-phase-deadlines (Req 16, 17): server-computed derived lock/phase state. */
  effectiveLock?: boolean | null;
  isPhase1Open?: boolean | null;
  isPhase2Unlocked?: boolean | null;
}

interface RawPageApproval {
  applicantPersonId: string;
  competitionId: string;
  formPageId: string;
  status: PageApproval['status'];
  approvalDetails?: string | null;
}

// ---------------------------------------------------------------------------
// Mappers (wire → transport DTO).
// ---------------------------------------------------------------------------

/** Map the GraphQL OnboardingDestination to the Destination discriminated union (Req 15). */
function mapDestination(raw: RawOnboardingDestination | null): Destination {
  if (!raw) return { kind: 'DASHBOARD' };
  switch (raw.kind) {
    case 'CONTACT_INTAKE':
      return { kind: 'CONTACT_INTAKE' };
    case 'APPLICATION':
      return { kind: 'APPLICATION', competitionId: raw.competitionId ?? '' };
    case 'RESUME_RESPONSE':
      return { kind: 'RESUME_RESPONSE', competitionId: raw.competitionId ?? '' };
    case 'APPLICATION_STATUS':
      return { kind: 'APPLICATION_STATUS', competitionId: raw.competitionId ?? '' };
    case 'DASHBOARD':
    default:
      return { kind: 'DASHBOARD' };
  }
}

/** Decode a FormVersion's AWSJSON snapshot into the structured FormVersionSnapshot (Req 8). */
function mapFormVersion(raw: RawFormVersion): FormVersion {
  return {
    id: raw.id,
    formSchemaId: raw.formSchemaId,
    versionNumber: raw.versionNumber,
    competitionId: raw.competitionId ?? null,
    snapshot: decodeAwsJson<FormVersionSnapshot>(raw.snapshot),
    createdAt: raw.createdAt,
  };
}

/** Decode an ApplicationResponse's AWSJSON `values` into the typed ResponseValues map (Req 6, 7). */
function mapApplicationResponse(raw: RawApplicationResponse): ApplicationResponse {
  return {
    applicantPersonId: raw.applicantPersonId,
    competitionId: raw.competitionId,
    formSchemaId: raw.formSchemaId,
    formVersionId: raw.formVersionId,
    submissionStatus: raw.submissionStatus,
    values: decodeAwsJson<ResponseValues>(raw.values),
    titlePreferences: raw.titlePreferences ?? null,
    // The director "request changes" marker — threaded onto the owner DTO so the
    // /apply surface resolves CHANGES_REQUESTED for a returned application
    // (applicant-application-flow); false/absent when never returned.
    reviewReturn: raw.reviewReturn ?? false,
    // application-decision-feedback (Req 2.4): the contestant-visible return note;
    // the director-only internalRejectNote never reaches this owner DTO.
    returnFeedbackNote: raw.returnFeedbackNote ?? null,
    divisionKey: raw.divisionKey ?? null,
    approvedTitle: raw.approvedTitle ?? null,
    // season-phase-deadlines: the Director override + server-derived lock/phase
    // state the client surfaces mirror (Req 16, 17); null when unset/older backend.
    manualLockOverride: raw.manualLockOverride ?? null,
    effectiveLock: raw.effectiveLock ?? null,
    isPhase1Open: raw.isPhase1Open ?? null,
    isPhase2Unlocked: raw.isPhase2Unlocked ?? null,
    submittedAt: raw.submittedAt ?? null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

/** Project an ApplicationResponse to the director-review summary row (Req 9.1). */
function mapResponseSummary(raw: RawApplicationResponse): ApplicationResponseSummary {
  return {
    applicantPersonId: raw.applicantPersonId,
    competitionId: raw.competitionId,
    formVersionId: raw.formVersionId,
    // Resolved from the Person SSOT server-side by listApplicationResponses
    // (Req 9.1); null when unresolved, in which case the review view falls back
    // to the personId.
    applicantName: raw.applicantName ?? null,
    // Resolved from Participation server-side; drives the review list division tabs.
    divisionKey: raw.divisionKey ?? null,
    // The director-approved title (Participation SSOT) — powers the Approved Titles view.
    approvedTitle: raw.approvedTitle ?? null,
    // The applicant's ranked title preferences (Pending_Title_Intent) for the
    // review list (Req 12.2); already on the wire via APPLICATION_RESPONSE_FIELDS.
    titlePreferences: raw.titlePreferences ?? null,
    submissionStatus: raw.submissionStatus,
    submittedAt: raw.submittedAt ?? null,
    updatedAt: raw.updatedAt,
  };
}

/**
 * Project an ApplicationResponse to the read-only Approvals roll-up value row
 * (approval-rollup-surfaces Req 2.1, 2.2): the raw CUSTOM `values` map plus the
 * Person-resolved `applicantName` for row ordering (Req 2.8). The `values`
 * AWSJSON is decoded exactly like `mapApplicationResponse`.
 */
function mapResponseValuesRow(raw: RawApplicationResponse): ResponseValuesRow {
  return {
    applicantPersonId: raw.applicantPersonId,
    applicantName: raw.applicantName ?? null,
    values: decodeAwsJson<ResponseValues>(raw.values),
  };
}

/** Decode a PageApproval's nullable AWSJSON `approvalDetails` (Req 21.7). */
function mapPageApproval(raw: RawPageApproval): PageApproval {
  return {
    applicantPersonId: raw.applicantPersonId,
    competitionId: raw.competitionId,
    formPageId: raw.formPageId,
    status: raw.status,
    approvalDetails:
      raw.approvalDetails != null ? decodeAwsJson<ApprovalDetails>(raw.approvalDetails) : null,
  };
}

export const appSyncApplicationFormClient: ApplicationFormClient = {
  // --- Onboarding + contact intake (applicant; Req 14, 15) -------------------
  async getOnboardingDestination() {
    const raw = await run<RawOnboardingDestination | null>(
      ops.GET_ONBOARDING_DESTINATION,
      {},
      'getOnboardingDestination',
    );
    return mapDestination(raw);
  },

  async submitContactIntake(input) {
    const person = await run<{ personId: string }>(
      ops.SUBMIT_CONTACT_INTAKE,
      { input },
      'submitContactIntake',
    );
    return { personId: person.personId };
  },

  // --- Builder (director/admin; Req 1, 2, 3, 8, 20) --------------------------
  getApplicationForm(competitionId) {
    return run<ApplicationForm>(
      ops.GET_APPLICATION_FORM,
      { competitionId },
      'getApplicationForm',
    );
  },

  listFormPages(formSchemaId) {
    return run<FormPage[]>(ops.LIST_FORM_PAGES, { formSchemaId }, 'listFormPages');
  },

  createFormPage(input) {
    return run<FormPage>(ops.CREATE_FORM_PAGE, { input }, 'createFormPage');
  },

  updateFormPage(input) {
    return run<FormPage>(ops.UPDATE_FORM_PAGE, { input }, 'updateFormPage');
  },

  reorderFormPages(formSchemaId, orderedIds) {
    return run<FormPage[]>(
      ops.REORDER_FORM_PAGES,
      { formSchemaId, orderedIds },
      'reorderFormPages',
    );
  },

  deleteFormPage(id) {
    return run<string>(ops.DELETE_FORM_PAGE, { id }, 'deleteFormPage');
  },

  createFormField(input) {
    return run<FormField>(ops.CREATE_FORM_FIELD, { input }, 'createFormField');
  },

  updateFormField(input) {
    return run<FormField>(ops.UPDATE_FORM_FIELD, { input }, 'updateFormField');
  },

  reorderFormFields(formPageId, orderedIds) {
    return run<FormField[]>(
      ops.REORDER_FORM_FIELDS,
      { formPageId, orderedIds },
      'reorderFormFields',
    );
  },

  deleteFormField(id) {
    return run<string>(ops.DELETE_FORM_FIELD, { id }, 'deleteFormField');
  },

  async publishFormVersion(formSchemaId) {
    const raw = await run<RawFormVersion>(
      ops.PUBLISH_FORM_VERSION,
      { formSchemaId },
      'publishFormVersion',
    );
    return mapFormVersion(raw);
  },

  // --- Document catalog (director/admin; Req 20) -----------------------------
  getDocumentCatalog(competitionId) {
    return run<DocumentCatalogEntry[]>(
      ops.GET_DOCUMENT_CATALOG,
      { competitionId },
      'getDocumentCatalog',
    );
  },

  upsertDocumentCatalogEntry(input) {
    return run<DocumentCatalogEntry>(
      ops.UPSERT_DOCUMENT_CATALOG_ENTRY,
      { input },
      'upsertDocumentCatalogEntry',
    );
  },

  deleteDocumentCatalogEntry(competitionId, id) {
    return run<string>(
      ops.DELETE_DOCUMENT_CATALOG_ENTRY,
      { competitionId, id },
      'deleteDocumentCatalogEntry',
    );
  },

  reorderDocumentCatalogEntries(competitionId, orderedIds) {
    return run<DocumentCatalogEntry[]>(
      ops.REORDER_DOCUMENT_CATALOG_ENTRIES,
      { competitionId, orderedIds },
      'reorderDocumentCatalogEntries',
    );
  },

  // --- Document uploads (applicant; apply-document-uploads Req 2, 6, 7, 10) ---
  // Every op resolves the caller's `personId` from the authenticated Cognito
  // identity via `resolveCallerPersonId()` and passes it as the operations'
  // `applicantId` argument — the id is NEVER taken from a method argument, so a
  // caller can only ever read/write their own documents (Req 10.2, 10.3), and an
  // unauthenticated caller is rejected before any request is issued (Req 10.4).
  async listMyUploadedDocuments(competitionYear): Promise<DocumentRef[]> {
    const applicantId = await resolveCallerPersonId();
    const raw = await run<DocumentRef[] | null>(
      ops.LIST_DOCUMENT_REFERENCES,
      { applicantId, competitionYear },
      'listDocumentReferences',
    );
    return raw ?? [];
  },

  async getDocumentUploadUrl(input: GetDocumentUploadUrlInput): Promise<PresignedUpload> {
    const applicantId = await resolveCallerPersonId();
    return run<PresignedUpload>(
      ops.GET_DOCUMENT_UPLOAD_URL,
      {
        input: {
          applicantId,
          competitionYear: input.competitionYear,
          documentType: input.documentType,
          fileName: input.fileName,
          contentType: input.contentType,
          catalogEntryId: input.catalogEntryId,
        },
      },
      'getDocumentUploadUrl',
    );
  },

  async createDocumentReference(input: CreateDocumentReferenceInput): Promise<DocumentRef> {
    const applicantId = await resolveCallerPersonId();
    return run<DocumentRef>(
      ops.CREATE_DOCUMENT_REFERENCE,
      {
        input: {
          applicantId,
          competitionYear: input.competitionYear,
          documentType: input.documentType,
          s3Key: input.s3Key,
          fileName: input.fileName,
          contentType: input.contentType,
          catalogEntryId: input.catalogEntryId,
        },
      },
      'createDocumentReference',
    );
  },

  deleteDocumentReference(id) {
    // The delete mutation is keyed by the DocumentReference id; the server
    // authorizes ownership against the authenticated identity (Req 10.1, 10.4).
    return run<string>(ops.DELETE_DOCUMENT_REFERENCE, { id }, 'deleteDocumentReference');
  },

  // --- Renderer (applicant; Req 5, 6, 7) -------------------------------------
  // The schema query is keyed by competitionId only; the interface's
  // `divisionKey` is resolved server-side / by the renderer, so the
  // implementation simply omits it (a narrower arity is interface-compatible).
  async getActiveFormVersion(competitionId) {
    const raw = await run<RawFormVersion>(
      ops.GET_ACTIVE_FORM_VERSION,
      { competitionId },
      'getActiveFormVersion',
    );
    return mapFormVersion(raw);
  },

  async getMyApplicationResponse(competitionId) {
    const raw = await run<RawApplicationResponse | null>(
      ops.GET_MY_APPLICATION_RESPONSE,
      { competitionId },
      'getMyApplicationResponse',
    );
    return raw ? mapApplicationResponse(raw) : null;
  },

  async getMyPageApprovals(competitionId): Promise<PageApproval[]> {
    // The AUTHENTICATED caller's OWN page approvals — the applicant is resolved
    // server-side from the Cognito identity, so no personId is sent (self-scoped,
    // Req 21). Each row's `approvalDetails` is nullable AWSJSON, decoded via
    // `mapPageApproval` exactly like `setPageApproval`.
    const raw = await run<RawPageApproval[] | null>(
      ops.GET_MY_PAGE_APPROVALS,
      { competitionId },
      'getMyPageApprovals',
    );
    return (raw ?? []).map(mapPageApproval);
  },

  async saveDraft(input) {
    // `values` is an AWSJSON input scalar — send it as a JSON-encoded string.
    const variables = {
      input: {
        competitionId: input.competitionId,
        formVersionId: input.formVersionId,
        values: JSON.stringify(input.values),
        titlePreferences: input.titlePreferences ?? null,
      },
    };
    const raw = await run<RawApplicationResponse>(
      ops.SAVE_APPLICATION_RESPONSE_DRAFT,
      variables,
      'saveApplicationResponseDraft',
    );
    return mapApplicationResponse(raw);
  },

  async submit(input): Promise<SubmitResult> {
    // `values` is an AWSJSON input scalar — send it as a JSON-encoded string.
    // The schema returns a non-null ApplicationResponse: a successful submit
    // yields the SUBMITTED response; a server-side block surfaces as a GraphQL
    // error (full client validation + the document gate run before this call,
    // so a server block is exceptional). On success → { ok: true, response }.
    const variables = {
      input: {
        competitionId: input.competitionId,
        formVersionId: input.formVersionId,
        values: JSON.stringify(input.values),
        titlePreferences: input.titlePreferences ?? null,
      },
    };
    const raw = await run<RawApplicationResponse>(
      ops.SUBMIT_APPLICATION_RESPONSE,
      variables,
      'submitApplicationResponse',
    );
    return { ok: true, response: mapApplicationResponse(raw) };
  },

  // --- Bound-field writes route to canonical writers (Req 17, 18, 19) --------
  async selectDivision(competitionId, divisionKey) {
    // Routes to Participation.divisionKey; the returned Participation is the
    // canonical write receipt — the seam resolves to void (Req 18).
    await run<unknown>(ops.SELECT_DIVISION, { competitionId, divisionKey }, 'selectDivision');
  },

  async selectSeason(competitionId, competitionYear) {
    // Routes to Participation.competitionYear (lazily creating the Participation
    // when none exists), mirroring selectDivision; the returned Participation is
    // the canonical write receipt — the seam resolves to void
    // (director-form-application-cutover Req 4.2).
    await run<unknown>(ops.SELECT_SEASON, { competitionId, competitionYear }, 'selectSeason');
  },

  async saveTitlePreferences(competitionId, ranked) {
    // Persists the ranked preferences on the response slot (Req 19.2). The
    // mutation returns the ApplicationResponse, not the Title_Contention_Warning
    // list — the server runs `titleContentionWarnings` non-blocking but does not
    // surface it over this operation, so the live seam returns an empty
    // contention list. (Mirrors the documented Merit live-client backend gaps;
    // the mock computes contention from the shared pure module.)
    await run<unknown>(
      ops.SAVE_TITLE_PREFERENCES,
      { competitionId, ranked },
      'saveTitlePreferences',
    );
    return { contention: [] };
  },

  // --- Season-scoped Title catalog read (season-scoped-title-registry Req 5.1)
  async listTitles(competitionYear): Promise<Title[]> {
    // Reads the active per-competitionYear Title_Catalog, ordered server-side by
    // displayOrder. The scalar Title shape needs no AWSJSON decoding.
    const raw = await run<Title[] | null>(
      ops.LIST_TITLES,
      { competitionYear },
      'listTitles',
    );
    return raw ?? [];
  },

  // --- Review + approvals (director; Req 9, 19, 21) --------------------------
  async listResponses(competitionId) {
    const raw = await run<RawApplicationResponse[]>(
      ops.LIST_APPLICATION_RESPONSES,
      { competitionId },
      'listApplicationResponses',
    );
    return (raw ?? []).map(mapResponseSummary);
  },

  async listResponseValues(competitionId): Promise<ResponseValuesRow[]> {
    // Read-only Approvals roll-up read (approval-rollup-surfaces Req 2.1, 2.2):
    // every contestant's raw CUSTOM `values` map plus the Person-resolved
    // `applicantName`. Reuses the director review-list query, which already
    // selects `values` + `applicantName`; the roll-up joins `values` locally
    // against the target page's current CUSTOM fields. No write path (Req 4.1).
    const raw = await run<RawApplicationResponse[]>(
      ops.LIST_APPLICATION_RESPONSES,
      { competitionId },
      'listApplicationResponses',
    );
    return (raw ?? []).map(mapResponseValuesRow);
  },

  async listPageApprovalsForPage(competitionId, formPageId): Promise<PageApproval[]> {
    // Read-only Approvals roll-up status read (approval-rollup-surfaces Req 3.1):
    // every contestant's PageApproval for ONE approval page, via the
    // competitionPage-applicantPersonId-index GSI (PK `${competitionId}#${formPageId}`),
    // so records for a different competition or page are excluded by construction.
    // Each row's `approvalDetails` is nullable AWSJSON, decoded via
    // `mapPageApproval`. No write path (Req 4.1, 4.8).
    const raw = await run<RawPageApproval[] | null>(
      ops.LIST_PAGE_APPROVALS_FOR_PAGE,
      { competitionId, formPageId },
      'listPageApprovalsForPage',
    );
    return (raw ?? []).map(mapPageApproval);
  },

  async listPageApprovals(competitionId, applicantPersonId): Promise<PageApproval[]> {
    // Director Review Detail read (Req 21.2, 21.4, 21.7): one applicant's
    // PageApproval rows across every approval-required page. Same decode as
    // the other PageApproval reads; a page with no row defaults to PENDING on
    // the caller side. No write path.
    const raw = await run<RawPageApproval[] | null>(
      ops.LIST_PAGE_APPROVALS,
      { competitionId, applicantPersonId },
      'listPageApprovals',
    );
    return (raw ?? []).map(mapPageApproval);
  },

  async getResponseForReview(competitionId, applicantPersonId): Promise<RenderedResponse> {
    // The server renders the captured response through its recorded FormVersion
    // (bound fields resolved from the Person/Participation/document canonical
    // sources) and returns the full review envelope as AWSJSON (Req 9.1, 9.3).
    const raw = await run<string | null>(
      ops.GET_RENDERED_RESPONSE_FOR_REVIEW,
      { competitionId, applicantPersonId },
      'getRenderedResponseForReview',
    );
    if (raw == null) {
      return {
        applicantPersonId,
        competitionId,
        submissionStatus: 'DRAFT',
        isDraft: true,
        sections: [],
      };
    }
    const envelope = decodeAwsJson<{
      applicantPersonId?: string;
      competitionId?: string;
      submissionStatus?: RenderedResponse['submissionStatus'];
      isDraft?: boolean;
      sections?: RenderedResponse['sections'];
      reviewReturn?: boolean;
      applicantName?: string | null;
      profilePhotoS3Key?: string | null;
      divisionKey?: string | null;
      titlePreferences?: string[];
      approvedTitle?: string | null;
      returnFeedbackNote?: string | null;
      internalRejectNote?: string | null;
    }>(raw);
    const submissionStatus = envelope.submissionStatus ?? 'DRAFT';
    return {
      applicantPersonId: envelope.applicantPersonId ?? applicantPersonId,
      competitionId: envelope.competitionId ?? competitionId,
      submissionStatus,
      isDraft: envelope.isDraft ?? submissionStatus === 'DRAFT',
      sections: envelope.sections ?? [],
      reviewReturn: envelope.reviewReturn ?? false,
      applicantName: envelope.applicantName ?? null,
      profilePhotoS3Key: envelope.profilePhotoS3Key ?? null,
      divisionKey: envelope.divisionKey ?? null,
      titlePreferences: envelope.titlePreferences ?? [],
      approvedTitle: envelope.approvedTitle ?? null,
      // application-decision-feedback (Req 5.4): the director-gated review
      // envelope carries BOTH the contestant-visible return note and the
      // director-only internal reject note.
      returnFeedbackNote: envelope.returnFeedbackNote ?? null,
      internalRejectNote: envelope.internalRejectNote ?? null,
    };
  },

  async getMyRenderedResponse(competitionId): Promise<RenderedResponse> {
    // Self-scoped counterpart of getResponseForReview: the server resolves the
    // applicant from the caller's OWN Cognito identity (never a client-supplied
    // id) and never serializes returnFeedbackNote/internalRejectNote onto this
    // envelope (self-service PDF export).
    const raw = await run<string | null>(
      ops.GET_MY_RENDERED_RESPONSE,
      { competitionId },
      'getMyRenderedResponse',
    );
    if (raw == null) {
      return {
        applicantPersonId: '',
        competitionId,
        submissionStatus: 'DRAFT',
        isDraft: true,
        sections: [],
      };
    }
    const envelope = decodeAwsJson<{
      applicantPersonId?: string;
      competitionId?: string;
      submissionStatus?: RenderedResponse['submissionStatus'];
      isDraft?: boolean;
      sections?: RenderedResponse['sections'];
      reviewReturn?: boolean;
      applicantName?: string | null;
      profilePhotoS3Key?: string | null;
      divisionKey?: string | null;
      titlePreferences?: string[];
      approvedTitle?: string | null;
    }>(raw);
    const submissionStatus = envelope.submissionStatus ?? 'DRAFT';
    return {
      applicantPersonId: envelope.applicantPersonId ?? '',
      competitionId: envelope.competitionId ?? competitionId,
      submissionStatus,
      isDraft: envelope.isDraft ?? submissionStatus === 'DRAFT',
      sections: envelope.sections ?? [],
      reviewReturn: envelope.reviewReturn ?? false,
      applicantName: envelope.applicantName ?? null,
      profilePhotoS3Key: envelope.profilePhotoS3Key ?? null,
      divisionKey: envelope.divisionKey ?? null,
      titlePreferences: envelope.titlePreferences ?? [],
      approvedTitle: envelope.approvedTitle ?? null,
    };
  },

  async reviewApplicationResponse(competitionId, applicantPersonId, decision, note) {
    // Director decision on a SUBMITTED application; the returned response is the
    // write receipt — the seam resolves to void (the caller refetches).
    await run<unknown>(
      ops.REVIEW_APPLICATION_RESPONSE,
      { competitionId, applicantPersonId, decision, note: note ?? null },
      'reviewApplicationResponse',
    );
  },

  async setApprovedTitle(competitionId, applicantPersonId, title) {
    // Writes Participation.approvedTitle (director/admin only); the returned
    // Participation is the write receipt — the seam resolves to void (Req 19.5).
    await run<unknown>(
      ops.SET_APPROVED_TITLE,
      { competitionId, applicantPersonId, title },
      'setApprovedTitle',
    );
  },

  async setPageApproval(input) {
    // `approvalDetails` is a nullable AWSJSON input — send it as a JSON string.
    const variables = {
      input: {
        applicantPersonId: input.applicantPersonId,
        competitionId: input.competitionId,
        formPageId: input.formPageId,
        status: input.status,
        approvalDetails: input.approvalDetails ? JSON.stringify(input.approvalDetails) : null,
      },
    };
    const raw = await run<RawPageApproval>(
      ops.SET_PAGE_APPROVAL_STATUS,
      variables,
      'setPageApprovalStatus',
    );
    return mapPageApproval(raw);
  },

  // --- Manual lock override (director; season-phase-deadlines Req 16, 18) ----
  async setApplicationLockOverride(
    competitionId,
    applicantPersonId,
    override,
  ): Promise<ApplicationResponse> {
    // `override` is a nullable ManualLockOverride enum arg — passing null clears
    // it (server REMOVEs the attribute). The returned ApplicationResponse echoes
    // the override and carries the server-computed derived lock/phase fields.
    const raw = await run<RawApplicationResponse>(
      ops.SET_APPLICATION_LOCK_OVERRIDE,
      { competitionId, applicantPersonId, override: override ?? null },
      'setApplicationLockOverride',
    );
    return mapApplicationResponse(raw);
  },

  async setApplicationLockOverridesForCompetition(
    competitionId,
    override,
  ): Promise<ApplicationResponse[]> {
    const raw = await run<RawApplicationResponse[] | null>(
      ops.SET_APPLICATION_LOCK_OVERRIDES_FOR_COMPETITION,
      { competitionId, override: override ?? null },
      'setApplicationLockOverridesForCompetition',
    );
    return (raw ?? []).map(mapApplicationResponse);
  },

  // --- Merit projection (judge; Req 21.8, 22.4) ------------------------------
  async getJudgeProjection(competitionId, applicantPersonId): Promise<ApplicationSection[]> {
    // The projection crosses the wire as AWSJSON (the ApplicationSection[]
    // contract the existing ContestantApplicationView consumes).
    const raw = await run<string | null>(
      ops.GET_JUDGE_PROJECTION,
      { competitionId, applicantPersonId },
      'getJudgeProjection',
    );
    if (raw == null) return [];
    return decodeAwsJson<ApplicationSection[]>(raw);
  },

  // --- Approval-detail judge visibility (director; Req 22.7, 21.8) -----------
  async getApprovalDetailVisibility(competitionId) {
    const raw = await run<ApprovalDetailVisibilityConfig[] | null>(
      ops.GET_APPROVAL_DETAIL_VISIBILITY,
      { competitionId },
      'getApprovalDetailVisibility',
    );
    return raw ?? [];
  },

  async setApprovalDetailVisibility(input) {
    return run<ApprovalDetailVisibilityConfig>(
      ops.SET_APPROVAL_DETAIL_VISIBILITY,
      { input },
      'setApprovalDetailVisibility',
    );
  },
};
